import { RefAttributes, useState } from 'react';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Key } from 'react';
import {
  Card,
  Col,
  OverlayTrigger,
  Row,
  Tooltip,
  TooltipProps,
} from 'react-bootstrap';
import Markdown from 'react-markdown';
import { MdExpandMore, MdExpandLess, MdOutlineEdit } from 'react-icons/md';
import { BsMicrosoftTeams } from 'react-icons/bs';

import { IncidentCalendarDisplayEvent } from './hooks/incidentCalendarHooks';

dayjs.extend(utc);

type EventCardProps = {
  seg: IncidentCalendarDisplayEvent;
  key: Key;
  eventClickCallback: (id: string) => void;
  expanded?: boolean;
};

function getSafeTeamsJoinUrl(rawUrl?: string | null): string | null {
  if (!rawUrl) {
    return null;
  }

  try {
    const url = new URL(rawUrl);

    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return null;
    }

    const hostname = url.hostname.toLowerCase();
    const allowedHosts = [
      'teams.microsoft.com',
      'teams.live.com',
      'teams.microsoft.us',
      'gov.teams.microsoft.us',
    ];

    const isAllowedHost =
      allowedHosts.includes(hostname) ||
      allowedHosts.some((allowed) => hostname.endsWith('.' + allowed));

    if (!isAllowedHost) {
      return null;
    }

    return url.toString();
  } catch {
    return null;
  }
}

function EventCard({
  seg,
  key: index,
  eventClickCallback,
  expanded: externalExpanded = false,
}: EventCardProps) {
  const [expanded, setExpanded] = useState(externalExpanded);
  const safeTeamsJoinUrl = getSafeTeamsJoinUrl(seg.teamsJoinUrl);
  const startValue: Date | string | undefined = seg.start;
  const eventStart = !startValue
    ? null
    : startValue instanceof Date
      ? dayjs(startValue)
      : dayjs.utc(startValue);

  const renderTooltip = (
    props: JSX.IntrinsicAttributes &
      TooltipProps &
      RefAttributes<HTMLDivElement>,
    eventTypeLabel: string
  ) => (
    <Tooltip id="tooltip-top" {...props}>
      {eventTypeLabel}
    </Tooltip>
  );

  return (
    <Card className="bg-bgdark m-3" key={index}>
      <Card.Body
        style={{
          paddingTop: '0px',
          paddingBottom: '0px',
          backgroundColor: '#343a40',
          borderRadius: '5px',
        }}
      >
        <Row>
          <OverlayTrigger
            placement="top" // or 'bottom', 'left', 'right'
            delay={{ show: 250, hide: 400 }} // Optional: delay before showing/hiding
            overlay={(props) => renderTooltip(props, seg.eventTypeLabel)} // Tooltip content
          >
            <Col
              style={{
                backgroundColor: seg.color,
                borderRadius: '5px 0px 0px 5px',
                flexGrow: 0,
                paddingLeft: '10px',
                paddingRight: '10px',
                paddingTop: '0px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
              }}
            >
              <span style={{ fontWeight: 'bold' }}>
                {eventStart?.format('MMM')}
              </span>
              <span style={{ fontSize: 23, fontWeight: 'bold' }}>
                {eventStart?.format('D')}
              </span>
            </Col>
          </OverlayTrigger>
          <Col
            style={{
              padding: '15px',
            }}
          >
            <b>{seg.title}</b>
            <div className="text-muted">
              {eventStart && <div>Time: {eventStart.format('HH:mm')}</div>}
              {seg.location && <div>Location: {seg.location}</div>}
              {safeTeamsJoinUrl && (
                <a
                  href={safeTeamsJoinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm d-inline-flex align-items-center mt-1 gap-1"
                  style={{ backgroundColor: '#6264A7', color: '#fff' }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = '#464775')
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = '#6264A7')
                  }
                >
                  <BsMicrosoftTeams /> Join
                </a>
              )}
            </div>
          </Col>
          <Col
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'right',
              gap: '10px',
            }}
          >
            <StylishNewButton
              className="button--secondary"
              onClick={() => eventClickCallback(seg.id)}
              title="Edit Event"
            >
              <MdOutlineEdit />
            </StylishNewButton>
            <StylishNewButton
              className="button--secondary"
              onClick={() => setExpanded(!expanded)}
              title={expanded ? 'Hide Agenda' : 'Show Agenda'}
            >
              {expanded && seg.purpose ? <MdExpandLess /> : <MdExpandMore />}
            </StylishNewButton>
          </Col>
        </Row>

        {expanded && seg.purpose && (
          <Row>
            <Col style={{ margin: '10px 0' }}>
              <div>Agenda:</div>
              <Markdown>{seg.purpose}</Markdown>
            </Col>
          </Row>
        )}
      </Card.Body>
    </Card>
  );
}

export default EventCard;
